import React, { createContext, useContext, useEffect, useState } from 'react'
import { Appearance, Platform } from 'react-native'
import { RELAYABLY_THEME_STORAGE_KEY, darkTheme, lightTheme, Theme, ThemeMode } from './theme'

const IS_WEB = Platform.OS === 'web'

type ThemeContextValue = {
  theme: Theme
  themeMode: ThemeMode
  setThemeMode: (mode: ThemeMode) => void
}

const ThemeContext = createContext<ThemeContextValue | null>(null)

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [themeMode, setThemeMode] = useState<ThemeMode>(() => {
    if (IS_WEB && typeof window !== 'undefined') {
      const saved = window.localStorage.getItem(RELAYABLY_THEME_STORAGE_KEY)
      if (saved === 'light' || saved === 'dark' || saved === 'system') {
        return saved
      }
    }
    return 'system'
  })

  const systemScheme = Appearance.getColorScheme()
  const isDark = themeMode === 'system' ? systemScheme === 'dark' : themeMode === 'dark'
  const theme = isDark ? darkTheme : lightTheme

  useEffect(() => {
    if (!IS_WEB || typeof window === 'undefined' || typeof document === 'undefined') {
      return
    }

    const linkId = 'relayably-google-fonts'
    if (!document.getElementById(linkId)) {
      const link = document.createElement('link')
      link.id = linkId
      link.rel = 'stylesheet'
      link.href =
        'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&family=JetBrains+Mono:wght@500;700&display=swap'
      document.head.appendChild(link)
    }

    document.body.style.margin = '0'
    document.body.style.backgroundColor = theme.colors.background
    document.body.style.color = theme.colors.text
    window.localStorage.setItem(RELAYABLY_THEME_STORAGE_KEY, themeMode)
  }, [theme.colors.background, theme.colors.text, themeMode])

  return <ThemeContext.Provider value={{ theme, themeMode, setThemeMode }}>{children}</ThemeContext.Provider>
}

export function useThemeValue() {
  const context = useContext(ThemeContext)
  if (!context) {
    throw new Error('ThemeContext missing')
  }
  return context
}
